import axios from "axios";
import { useSnackbar } from "material-ui-snackbar-provider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AutocompleteChangeReason,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";

import { APIEndpoints } from "@/apis/endpoints";
import { BigSpinner } from "@/components/Spinner";
import { ConfirmationDialog } from "@/components/modals/ConfirmationDialog";
import SaaSSelector from "@/pages/general-settings/commonUserProvisioning/DepartmentsSaas/SaaSSelector";
import TemplateAttributionSelector from "@/pages/general-settings/commonUserProvisioning/DepartmentsSaas/TemplateAttributionSelector";
import { extractErrorMessage } from "@/utils/misc";

export function TemplateAttribution() {
  const [departments, setDepartments] = useState([]);
  const [saasList, setSaasList] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [companySaasList, setCompanySaasList] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [isLoadingSaasList, setIsLoadingSaasList] = useState(true);
  const [isLoadingCompanySaasList, setIsLoadingCompanySaasList] =
    useState(true);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const snackbar = useSnackbar();
  const { t } = useTranslation();

  const onUpdateSaas = (
    newSaasList: { id: number; name: string }[],
    reason: AutocompleteChangeReason,
  ) => {
    const f = async () => {
      if (reason === "clear") {
        setConfirmationOpen(true);
      } else {
        try {
          const result = await axios.put(
            APIEndpoints.SIMULATION + "/company/saas",
            newSaasList,
          );
          setCompanySaasList(result.data);
          setErrorMessage(null);
          snackbar.showMessage("Company saas updated");
        } catch (error) {
          setErrorMessage(extractErrorMessage(error));
        }
      }
    };
    // noinspection JSIgnoredPromiseFromCall
    f();
  };

  useEffect(() => {
    const f = async () => {
      try {
        const result = await axios.get(
          APIEndpoints.SIMULATION + "/company/saas",
        );

        setCompanySaasList(result.data);
        setErrorMessage(null);
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
      } finally {
        setIsLoadingCompanySaasList(false);
      }
    };
    // noinspection JSIgnoredPromiseFromCall
    f();
  }, [setIsLoadingCompanySaasList]);

  useEffect(() => {
    const f = async () => {
      try {
        const result = await axios.get(
          APIEndpoints.SIMULATION + "/departments",
        );

        setDepartments(result.data);
        setErrorMessage(null);
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    // noinspection JSIgnoredPromiseFromCall
    f();
  }, [setIsLoadingDepartments]);

  useEffect(() => {
    const f = async () => {
      if (departments.length > 0) {
        try {
          await axios.put(
            APIEndpoints.SIMULATION + "/departments",
            departments,
          );
          setErrorMessage(null);
        } catch (error) {
          setErrorMessage(extractErrorMessage(error));
        }
      }
    };

    f();
  }, [departments]);

  useEffect(() => {
    const f = async () => {
      try {
        const result = await axios.get(APIEndpoints.SIMULATION + "/saas");

        setSaasList(result.data);
        setErrorMessage(null);
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
      } finally {
        setIsLoadingSaasList(false);
      }
    };
    // noinspection JSIgnoredPromiseFromCall
    f();
  }, [setIsLoadingSaasList]);

  if (isLoadingSaasList || isLoadingDepartments || isLoadingCompanySaasList) {
    return <BigSpinner />;
  }

  return (
    <Card>
      <CardContent>
        <Box mt={2}>
          <Typography style={{ color: "black" }}>
            {t("Select the templates used by each department.")}
          </Typography>
        </Box>
        {errorMessage !== null && (
          <Typography style={{ color: "red" }}>{errorMessage}</Typography>
        )}

        <div className="row">
          <div className="col-md-6">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("Department")}</th>
                  <th scope="col">{t("Used Template tags")}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ width: "20%" }}>{t("Everyone")}</td>
                  <td>
                    <TemplateAttributionSelector
                      saasList={saasList}
                      companySaasList={companySaasList}
                      onChange={onUpdateSaas}
                    />
                  </td>
                </tr>
                {departments.map((department) => (
                  <tr key={department.id}>
                    <td style={{ width: "20%" }}>{department.name}</td>
                    <td>
                      <SaaSSelector
                        department={department}
                        departments={departments}
                        setDepartments={setDepartments}
                        saasList={saasList}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <ConfirmationDialog
                isShown={confirmationOpen}
                onConfirm={() => {
                  onUpdateSaas([], "removeOption");
                  setConfirmationOpen(false);
                }}
                onConfirmText={t("Confirm")}
                onCancel={() => setConfirmationOpen(false)}
                onCancelText={t("Cancel")}
                title={t("Are you sure ?")}
                content={t("This will remove all of these tags.")}
              />
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
